import { useMemo } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CheckBox from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import styles from "./styles";

// Rótulo curto de disponibilidade (true/false/null) para os chips de status
const rotuloDisponibilidade = (valor) =>
  valor === true ? "Up" : valor === false ? "Down" : "N/D";

const corChip = (valor) =>
  valor === true ? "success" : valor === false ? "error" : "default";

// Resumo "N interface(s) selecionada(s)" exibido no campo no lugar dos chips, pra não estourar
// o campo quando muitas interfaces forem marcadas
const rotuloSelecionadas = (quantidade) =>
  quantidade > 0
    ? `${quantidade} interface${quantidade > 1 ? "s" : ""} selecionada${quantidade > 1 ? "s" : ""}`
    : "";

const iconeDesmarcado = <CheckBoxOutlineBlank fontSize="small" />;
const iconeMarcado = <CheckBox fontSize="small" />;

// Vínculo opcional de um node do diagrama a um host Zabbix (para consultar status ao vivo) e,
// quando há host vinculado, a uma ou mais interfaces já cadastradas nele. A interface é só uma
// etiqueta de identificação — nada é consultado a respeito dela, só a lista de sugestões já vem
// dentro do próprio hostOptions (hostsClienteInterfaces), sem requisição extra
export default function HostLinkSelector({
  hostId = "",
  interfaceValues = [],
  onChangeHost = () => {},
  onChangeInterface = () => {},
  hostOptions = [],
  hostStatus = null,
  disabled = false,
}) {
  const theme = useTheme();
  const linkStyles = styles(theme);

  // groupBy exige que as opções do mesmo grupo venham contíguas
  const options = useMemo(
    () =>
      [...(hostOptions || [])].sort((a, b) =>
        (a?.funcao || "").localeCompare(b?.funcao || ""),
      ),
    [hostOptions],
  );

  const hostSelecionado =
    options.find((opcao) => opcao?.hostId === hostId) || null;
  const interfaceOptions = hostSelecionado?.hostsClienteInterfaces || [];

  return (
    <Box sx={linkStyles.container}>
      <Autocomplete
        size="small"
        fullWidth
        disabled={disabled}
        options={options}
        value={hostSelecionado}
        groupBy={(opcao) => opcao?.funcao?.trim?.() || "Outros"}
        getOptionLabel={(opcao) => opcao?.host || ""}
        isOptionEqualToValue={(opcao, valor) => opcao?.hostId === valor?.hostId}
        onChange={(_evento, novoValor) => onChangeHost(novoValor?.hostId || "")}
        ListboxProps={{ sx: linkStyles.listbox }}
        renderOption={(props, opcao) => (
          <Box component="li" {...props} key={opcao?.id || opcao?.hostId}>
            <Typography noWrap sx={linkStyles.optionLabel}>
              {opcao?.host}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Host vinculado" autoComplete="off" />
        )}
        noOptionsText="Nenhum host encontrado para este serviço"
      />
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        fullWidth
        freeSolo
        disabled={disabled || !hostSelecionado}
        options={interfaceOptions}
        value={interfaceValues}
        onChange={(_evento, novoValor) => onChangeInterface(novoValor || [])}
        renderOption={(props, opcao, { selected }) => (
          <Box component="li" {...props} key={opcao}>
            <Checkbox
              icon={iconeDesmarcado}
              checkedIcon={iconeMarcado}
              checked={selected}
              size="small"
              sx={linkStyles.optionCheckbox}
            />
            <Typography noWrap sx={linkStyles.optionLabel}>
              {opcao}
            </Typography>
          </Box>
        )}
        renderTags={(selecionadas) => (
          <Typography noWrap sx={linkStyles.selectedSummary}>
            {rotuloSelecionadas(selecionadas.length)}
          </Typography>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Interfaces"
            placeholder={
              !interfaceValues.length ? "Pesquisar interface..." : ""
            }
            autoComplete="off"
            helperText={
              !hostSelecionado
                ? "Vincule um host para ver as interfaces cadastradas"
                : ""
            }
          />
        )}
        noOptionsText="Nenhuma interface cadastrada para este host"
      />
      {!!hostSelecionado && (
        <Box sx={linkStyles.statusRow}>
          <Typography sx={linkStyles.statusLabel}>Status do host:</Typography>
          <Chip
            size="small"
            variant="outlined"
            color={corChip(hostStatus?.snmp)}
            label={`SNMP: ${rotuloDisponibilidade(hostStatus?.snmp)}`}
          />
          <Chip
            size="small"
            variant="outlined"
            color={corChip(hostStatus?.ping)}
            label={`PING: ${rotuloDisponibilidade(hostStatus?.ping)}`}
          />
        </Box>
      )}
    </Box>
  );
}
